import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, KeyRound, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { inputCls, labelCls, primaryBtn } from "@/components/portal/PortalAuth";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return setError("Passwords do not match");
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App flex min-h-screen items-center justify-center px-5" data-testid="reset-password-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md border border-white/10 bg-[#12151C] p-8 sm:p-10">
        <div className="font-display text-3xl tracking-[0.12em] text-slate-50">SAGITTARIUS</div>
        <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500">Choose a new password</div>

        {done ? (
          <div className="mt-8 border border-emerald-500/40 bg-emerald-500/10 px-4 py-4 text-sm text-emerald-300" data-testid="reset-success">
            Password updated. You can now sign in to the portal.
            <Link to="/portal" className="mt-4 block font-mono text-xs uppercase tracking-[0.2em] text-amber-500" data-testid="reset-go-portal">Go to portal →</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-form">
            <div>
              <label className={labelCls}>New password (min 8 characters)</label>
              <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="reset-password-input" />
            </div>
            <div>
              <label className={labelCls}>Confirm password</label>
              <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls} data-testid="reset-confirm-input" />
            </div>
            {!token && <div className="border border-red-500/50 bg-red-500/10 px-4 py-3 text-xs text-red-300">This link is missing its token. Request a new reset email.</div>}
            {error && <div className="border border-red-500/50 bg-red-500/10 px-4 py-3 text-xs text-red-300" data-testid="reset-error">{error}</div>}
            <button type="submit" disabled={loading || !token} className={primaryBtn} data-testid="reset-submit-button">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <KeyRound size={16} />} Update password
            </button>
          </form>
        )}

        <Link to="/portal" className="mt-8 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 transition-colors hover:text-amber-500" data-testid="reset-back">
          <ArrowLeft size={12} /> Back to portal
        </Link>
      </motion.div>
    </div>
  );
}
